// SdsLookupTab.js
// Safety page tab: header + TabSwitcher [Search | Requests] over the shared SdsLookupSection.

import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaSync } from "react-icons/fa";
import { useAppStore } from "../../../store/appStore";
import { useUserState } from "../../../context/user";
import { useSdsOnline } from "../useSdsOnline";
import { getSdsStatus, getSdsRequests, sdsErrorMessage } from "../../../api/SdsApi";
import { SdsLookupSection } from "../sdsLookupSection/SdsLookupSection";
import { MAX_TEXT_CHARS } from "../sdsLookupSection/useSdsSearch";
import { SdsRequestsList } from "./SdsRequestsList";
import { TabSwitcher, Button } from "../ui";
import listStyles from "../ui/listTab.module.css";
import styles from "./SdsLookupTab.module.css";

// Requests stays hidden until /api/sds/status reports request_enabled
const SDS_SUB_TABS = [
  { value: "search", label: "Search", testId: "sds-search-subtab" },
  { value: "requests", label: "Requests", testId: "sds-requests-subtab" },
];
const REQUESTS_PAGE_SIZE = 50;

export const SdsLookupTab = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isServerReachable } = useSdsOnline();
  const { currentUser } = useUserState();
  const showNotification = useAppStore((s) => s.notificationActions.showNotification);

  const [sdsStatus, setSdsStatus] = useState(null);
  const [activeSubTab, setActiveSubTab] = useState("search");
  const [requests, setRequests] = useState([]);
  const [requestsTotal, setRequestsTotal] = useState(0);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsLoaded, setRequestsLoaded] = useState(false);
  const [requestsError, setRequestsError] = useState(null);

  const params = new URLSearchParams(location.search || "");
  const requestedView = params.get("view");
  const initialQuery = params.get("q") || "";
  const requestEnabled = !!sdsStatus?.request_enabled;
  const visibleTabs = requestEnabled
    ? SDS_SUB_TABS
    : SDS_SUB_TABS.filter((tab) => tab.value !== "requests");
  const showRequests = activeSubTab === "requests" && requestEnabled;

  // Fetch the feature flags once we can reach the server; a failed fetch leaves search usable
  useEffect(() => {
    if (!isServerReachable || sdsStatus) return;
    let cancelled = false;
    getSdsStatus()
      .then((data) => {
        if (!cancelled && data) setSdsStatus(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isServerReachable, sdsStatus]);

  // ?view=requests opens the Requests sub-tab once the flag allows it
  useEffect(() => {
    if (requestedView === "requests" && requestEnabled) setActiveSubTab("requests");
    else if (requestedView !== "requests") setActiveSubTab("search");
  }, [requestedView, requestEnabled]);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    setRequestsError(null);
    try {
      const data = await getSdsRequests({ status: "all", page: 1, pageSize: REQUESTS_PAGE_SIZE });
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
      setRequestsTotal(Number(data?.pagination?.total) || 0);
    } catch (err) {
      setRequestsError(sdsErrorMessage(err));
    } finally {
      setRequestsLoading(false);
      setRequestsLoaded(true);
    }
  }, []);

  // Until the first load resolves the panel shows the spinner, not a flash of "No SDS requests yet."
  const requestsPending = requestsLoading || (!requestsLoaded && isServerReachable);

  // Reload whenever the Requests sub-tab becomes visible with a reachable server
  useEffect(() => {
    if (showRequests && isServerReachable) loadRequests();
  }, [showRequests, isServerReachable, loadRequests]);

  const handleSubTabChange = (value) => {
    setActiveSubTab(value);
    const next = new URLSearchParams(location.search || "");
    if (value === "requests") next.set("view", "requests");
    else next.delete("view");
    const search = next.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
  };

  // Requests -> Search handoff: the material name becomes ?q= so the section prefills and runs it.
  // Request names may run to 500 characters; the search box accepts 50, so the handoff is cut to fit.
  const handleSearchAgain = (request) => {
    const query = (request?.material_name || "").trim().slice(0, MAX_TEXT_CHARS).trim();
    const next = new URLSearchParams();
    if (query) next.set("q", query);
    const search = next.toString();
    setActiveSubTab("search");
    navigate({ pathname: location.pathname, search: search ? `?${search}` : "" }, { replace: true });
  };

  return (
    <div className={listStyles.tabContainer} data-testid="sds-lookup-tab">
      <div className={listStyles.tabContent}>
        <div className={listStyles.headerWithRefresh}>
          <h2>SDS Lookup</h2>
          {showRequests && (
            <Button
              variant="refresh"
              onClick={loadRequests}
              disabled={requestsLoading || !isServerReachable}
              title="Refresh SDS requests from server"
            >
              <FaSync className={requestsLoading ? styles.spinning : ""} />
              Refresh
            </Button>
          )}
        </div>

        <TabSwitcher
          tabs={visibleTabs}
          activeTab={activeSubTab}
          onTabChange={handleSubTabChange}
        />

        <div className={styles.content}>
          {showRequests ? (
            <div data-testid="sds-requests-panel">
              {!isServerReachable && (
                <div className={styles.requestsCount} role="status">
                  SDS requests need a connection. Showing the last loaded list.
                </div>
              )}
              {requestsTotal > requests.length && (
                <div className={styles.requestsCount}>
                  Showing the {requests.length} most recent of {requestsTotal} requests
                </div>
              )}
              <SdsRequestsList
                requests={requests}
                isLoading={requestsPending}
                error={requestsError}
                onRefresh={loadRequests}
                currentUserId={currentUser?.employee_id}
                onSearchAgain={handleSearchAgain}
              />
            </div>
          ) : (
            <SdsLookupSection
              isOnline={isServerReachable}
              sdsStatus={sdsStatus}
              initialQuery={initialQuery}
              showNotification={showNotification}
            />
          )}
        </div>
      </div>
    </div>
  );
};
